import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCardIcon,
  ChartBarIcon,
  CogIcon,
  DocumentTextIcon,
  ClockIcon,
  ChartPieIcon,
  CheckIcon,
  StarIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  DocumentIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAnalytics } from '../hooks/useAnalytics';
import { ButtonsContainer, GooglePlayButton, AppStoreButton } from 'react-mobile-app-button';

const features = [
  {
    name: 'AI-Powered Expense Tracking',
    description: 'Digitize receipts and automate expense tracking with our advanced AI technology.',
    icon: CreditCardIcon,
  },
  {
    name: 'Automated Invoice Management',
    description: 'Generate instant quotes and automate invoice follow-ups to improve cash flow.',
    icon: DocumentTextIcon,
  },
  {
    name: 'Real-Time Financial Insights',
    description: 'Get comprehensive reporting and clear insights into your business performance.',
    icon: ChartPieIcon,
  },
];

const testimonials = [
  {
    content: "Aided has transformed how we handle our business admin. The AI-powered features save us hours every week.",
    author: "Sarah Johnson",
    role: "CEO, TechStart Graphics",
    rating: 5,
  },
  {
    content: "The automated invoice management is a game-changer. We've reduced our payment collection time by 70%.",
    author: "Michael Chen",
    role: "Handyman, Michael's Handyman Services",
    rating: 5,
  },
  {
    content: "Finally, a solution that makes expense tracking painless! The AI accuracy is impressive.",
    author: "Emma Williams",
    role: "Plumber, Emma's Plumbing",
    rating: 5,
  },
];


export default function Landing() {
  const { logEvent } = useAnalytics();
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const heroFeatures = [
    {
      title: "AI-Powered Expense Tracking",
      description: "Digitize receipts and automate expense tracking with our advanced AI technology.",
      icon: CreditCardIcon,
      image: "/mockup-expenses.png",
      color: "#73D5D7"
    },
    {
      title: "Automated Invoice Management",
      description: "Generate instant quotes and automate invoice follow-ups to improve cash flow.",
      icon: DocumentIcon,
      image: "/mockup-invoice.png",
      color: "#818DD7"
    },
    {
      title: "Real-Time AI Help",
      description: "Get help creating quotes, invoices, and more with our AI assistant.",
      icon: ChartPieIcon,
      image: "/mockup-AI-help.png",
      color: "#6366F1"
    },
    {
      title: "Simple and intuitive",
      description: "Create a quote for your client in seconds. Just describe what you need and AI will do the rest.",
      icon: UserGroupIcon,
      image: "/mockup-quote.png",
      color: "#8B5CF6"
    }
  ];

  // Animation on scroll
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % heroFeatures.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => {
    logEvent('Landing', 'Get Started Click', 'CTA Button');
  };

  const handleFeatureClick = (featureName) => {
    logEvent('Landing', 'Feature Click', featureName);
  };

  const handlePricingClick = (plan) => {
    logEvent('Landing', 'Pricing Click', plan);
  };

  return (
    <div className="bg-white">
      {/* Hero section */}
      <div className="relative overflow-hidden min-h-screen flex items-center">
        {/* Background gradient - simplified without animated shapes */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, #73D5D7, #818DD7)' }}></div>

        <div className="max-w-7xl mx-auto relative z-10 w-full px-4 sm:px-6 lg:px-8 py-12">
          {/* Navigation */}
          <div className="absolute top-0 left-0 right-0 pt-8 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <div>
              <img src="/Invert.png" className="w-36 h-auto" alt="Aided Logo" />
            </div>
            <div className="flex items-center space-x-5">
                <Link
                  to="/login"
                className="text-white hover:text-gray-200 transition-colors text-base font-medium px-4 py-2 border border-white/30 rounded-full hover:bg-white/10"
              >
                Sign In
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center px-6 py-2.5 border border-transparent text-base font-medium rounded-full text-primary-dark bg-white hover:bg-gray-100 transition-all shadow-md hover:shadow-lg"
              >
                Get Started
                </Link>
              </div>
            </div>

          <main className="mt-28 sm:mt-32 md:mt-36 lg:mt-28 w-full">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
              {/* Left side content */}
              <div className={`text-center lg:text-left lg:w-1/2 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                  <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                    <span className="block">Your AI-Powered</span>
                  <span className="block bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">Business Admin Solution</span>
                  </h1>
                  <p className="mt-3 text-base text-gray-100 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                    Streamline your administrative tasks with AI automation. From expense tracking to invoice management, Aided handles it all.
                  </p>
                
                {/* Feature pills */}
                <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-3">
                  <button 
                    onClick={() => {
                      setActiveFeature(0);
                      handleFeatureClick("AI-Powered Expense Tracking");
                    }}
                    className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-white hover:bg-opacity-30 transition-all cursor-pointer text-xs sm:text-sm ${
                      activeFeature === 0 
                        ? 'bg-white bg-opacity-30 font-medium' 
                        : 'bg-white bg-opacity-20'
                    }`}
                  >
                    <CreditCardIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span>AI-Powered Expense Tracking</span>
                  </button>
                  <button 
                    onClick={() => {
                      setActiveFeature(1);
                      handleFeatureClick("Automated Invoice Management");
                    }}
                    className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-white hover:bg-opacity-30 transition-all cursor-pointer text-xs sm:text-sm ${
                      activeFeature === 1 
                        ? 'bg-white bg-opacity-30 font-medium' 
                        : 'bg-white bg-opacity-20'
                    }`}
                  >
                    <DocumentIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span>Automated Invoice Management</span>
                  </button>
                  <button 
                    onClick={() => {
                      setActiveFeature(2);
                      handleFeatureClick("Real-Time AI Help");
                    }}
                    className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-white hover:bg-opacity-30 transition-all cursor-pointer text-xs sm:text-sm ${
                      activeFeature === 2 
                        ? 'bg-white bg-opacity-30 font-medium' 
                        : 'bg-white bg-opacity-20'
                    }`}
                  >
                    <DevicePhoneMobileIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span>Real-Time AI Help</span>
                  </button>
                  <button 
                    onClick={() => {
                      setActiveFeature(3);
                      handleFeatureClick("Simple and intuitive");
                    }}
                    className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-white hover:bg-opacity-30 transition-all cursor-pointer text-xs sm:text-sm ${
                      activeFeature === 3 
                        ? 'bg-white bg-opacity-30 font-medium' 
                        : 'bg-white bg-opacity-20'
                    }`}
                  >
                    <UserGroupIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span>Simple and intuitive</span>
                  </button>
                </div>
                
                {/* CTA buttons 
                <div className="mt-10 flex flex-col sm:flex-row sm:justify-center lg:justify-start">
                  <div className="w-full sm:w-auto rounded-md shadow">
                    <Link
                      to="/signup"
                      onClick={handleGetStarted}
                      className="w-full flex items-center justify-center px-6 sm:px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary-dark bg-white hover:bg-gray-100 md:py-4 md:text-lg md:px-10 transition-all"
                    >
                      Get Started Free
                    </Link>
                  </div>
                  <div className="mt-4 sm:mt-0 sm:ml-3 w-full sm:w-auto">
                    <Link
                      to="/demo"
                      className="w-full flex items-center justify-center px-6 sm:px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary bg-opacity-60 hover:bg-opacity-70 md:py-4 md:text-lg md:px-10 transition-all"
                    >
                      Watch Demo
                    </Link>
                  </div>
                </div>
                */}
                
                {/* App store buttons */}
                <div className="mt-10">
                  <p className="text-sm text-gray-100 mb-3 text-center sm:text-left">Download our mobile app:</p>
                  <div className="flex flex-col sm:flex-row items-center sm:items-start justify-center lg:justify-start gap-3 sm:gap-4">
                    <a href="https://apps.apple.com/ph/app/aided/id6740941037" className="w-full sm:w-auto flex items-center justify-center sm:justify-start bg-black text-white rounded-lg px-4 py-2 transition-transform hover:scale-105">
                      <svg className="h-6 w-6 mr-2 flex-shrink-0" viewBox="0 0 384 512" fill="currentColor">
                        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                      </svg>
                      <div>
                        <div className="text-xs">Download on the</div>
                        <div className="text-lg font-semibold font-sans -mt-1">App Store</div>
                      </div>
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.aided.app" className="w-full sm:w-auto flex items-center justify-center sm:justify-start bg-black text-white rounded-lg px-4 py-2 transition-transform hover:scale-105">
                      <svg className="h-6 w-6 mr-2 flex-shrink-0" viewBox="0 0 512 512" fill="currentColor">
                        <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
                      </svg>
                      <div>
                        <div className="text-xs">GET IT ON</div>
                        <div className="text-lg font-semibold font-sans -mt-1">Google Play</div>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Right side - Phone mockup with feature showcase */}
              <div className={`mt-12 lg:mt-0 lg:w-1/2 transition-all duration-700 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                <div className="relative mx-auto w-full max-w-md">
                  {/* Phone frame */}
                  <div className="relative mx-auto w-[250px] sm:w-[280px] md:w-[300px] h-[500px] sm:h-[560px] md:h-[600px] bg-gray-900 rounded-[40px] border-[8px] sm:border-[10px] md:border-[12px] border-gray-900 shadow-2xl overflow-hidden">
                    
                    
                    {/* Phone content - Feature showcase */}
                    <div className="relative h-full w-full bg-white overflow-hidden rounded-[30px]">
                      {/* Active feature display */}
                      <div 
                        className="absolute inset-0 transition-all duration-500 transform"
                        style={{
                          background: `linear-gradient(to bottom right, ${heroFeatures[activeFeature].color}, ${heroFeatures[activeFeature].color}BB)`
                        }}
                      >
                        {/* App UI mockup */}
                        <div className="h-full flex flex-col">
                          {/* App header */}
                          <div className="px-4 py-6 flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center">
                                <img src="/gradient_logo.png" className="h-6 w-6" alt="Aided Logo" />
                              </div>
                              <span className="ml-2 text-white font-semibold">Aided</span>
                            </div>
                            <div className="flex space-x-2">
                              <div className="h-8 w-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                              </div>
                              <div className="h-8 w-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              </div>
                            </div>
                          </div>
                          
                          {/* Feature content */}
                          <div className="flex-1 px-4 py-6 flex flex-col items-center justify-center text-white">
                            {/* Dynamically render the icon component */}
                            {(() => {
                              const IconComponent = heroFeatures[activeFeature].icon;
                              return <IconComponent className="w-16 h-16 mb-4" />;
                            })()}
                            <h3 className="text-xl font-bold mb-2 text-center">{heroFeatures[activeFeature].title}</h3>
                            <p className="text-sm text-center mb-6">{heroFeatures[activeFeature].description}</p>
                            
                            {/* Feature-specific UI mockup */}
                            {activeFeature === 0 && (
                              <div className="w-full bg-white rounded-xl p-4 shadow-lg">
                                <div className="flex items-center justify-between mb-4">
                                  <div className="text-gray-800 font-medium">Recent Expenses</div>
                                  <div className="text-primary-dark text-sm">View All</div>
                                </div>
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <div className="flex items-center">
                                      <div className="h-10 w-10 rounded-full bg-primary-light/20 flex items-center justify-center text-primary-dark">
                                        <CreditCardIcon className="h-5 w-5" />
                                      </div>
                                      <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-800">Office Supplies</div>
                                        <div className="text-xs text-gray-500">Today, 2:30 PM</div>
                                      </div>
                                    </div>
                                    <div className="text-gray-800 font-medium">$24.99</div>
                                  </div>
                                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <div className="flex items-center">
                                      <div className="h-10 w-10 rounded-full bg-primary-light/20 flex items-center justify-center text-primary-dark">
                                        <CreditCardIcon className="h-5 w-5" />
                                      </div>
                                      <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-800">Client Lunch</div>
                                        <div className="text-xs text-gray-500">Yesterday</div>
                                      </div>
                                    </div>
                                    <div className="text-gray-800 font-medium">$42.50</div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {activeFeature === 1 && (
                              <div className="w-full bg-white rounded-xl p-4 shadow-lg">
                                <div className="flex items-center justify-between mb-4">
                                  <div className="text-gray-800 font-medium">Invoices</div>
                                  <div className="text-primary-dark text-sm">+ New</div>
                                </div>
                                <div className="space-y-3">
                                  <div className="p-3 border border-gray-200 rounded-lg">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="text-sm font-medium text-gray-800">INV-2023-001</div>
                                        <div className="text-xs text-gray-500">Johnson Residence</div>
                                        <div className="mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full inline-block">Paid</div>
                                      </div>
                                      <div className="text-gray-800 font-medium">$750.00</div>
                                    </div>
                                  </div>
                                  <div className="p-3 border border-gray-200 rounded-lg">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="text-sm font-medium text-gray-800">INV-2023-002</div>
                                        <div className="text-xs text-gray-500">Smith Office</div>
                                        <div className="mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full inline-block">Pending</div>
                                      </div>
                                      <div className="text-gray-800 font-medium">$1,250.00</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {activeFeature === 2 && (
                              <div className="w-full bg-white rounded-xl p-4 shadow-lg">
                                <div className="flex items-center mb-4">
                                  <div className="h-8 w-8 rounded-full bg-primary-light flex items-center justify-center text-white">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                  </div>
                                  <div className="ml-3 text-gray-800 font-medium">AI Assistant</div>
                                </div>
                                <div className="space-y-3">
                                  <div className="p-3 bg-gray-100 rounded-lg rounded-tl-none">
                                    <div className="text-sm text-gray-800">How can I help you today?</div>
                                  </div>
                                  <div className="p-3 bg-primary-light/20 rounded-lg rounded-tr-none text-right">
                                    <div className="text-sm text-gray-800">I need to create an invoice for a new client</div>
                                  </div>
                                  <div className="p-3 bg-gray-100 rounded-lg rounded-tl-none">
                                    <div className="text-sm text-gray-800">I can help with that! What services did you provide and what's the client's name?</div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {activeFeature === 3 && (
                              <div className="w-full bg-white rounded-xl p-4 shadow-lg">
                                <div className="text-gray-800 font-medium mb-4">Create Quote</div>
                                <div className="space-y-3">
                                  <div className="w-full">
                                    <label className="block text-xs text-gray-500 mb-1">Client</label>
                                    <div className="p-2 border border-gray-200 rounded-lg text-sm text-gray-800">
                                      Select client...
                                    </div>
                                  </div>
                                  <div className="w-full">
                                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                                    <div className="p-2 border border-gray-200 rounded-lg text-sm text-gray-800 h-20">
                                      Describe the work...
                                    </div>
                                  </div>
                                  <button className="w-full bg-primary-dark text-white py-2 rounded-lg text-sm font-medium">
                                    Generate with AI
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* App navigation */}
                          <div className="px-4 py-4 bg-white bg-opacity-10 backdrop-blur-lg flex items-center justify-around">
                            <button className="h-12 w-12 rounded-full flex items-center justify-center text-white">
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                              </svg>
                            </button>
                            <button className="h-12 w-12 rounded-full flex items-center justify-center text-white">
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                              </svg>
                            </button>
                            <button className="h-14 w-14 bg-white rounded-full -mt-6 flex items-center justify-center text-primary-dark shadow-lg border-4 border-primary-dark/20">
                              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                            <button className="h-12 w-12 rounded-full flex items-center justify-center text-white">
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </button>
                            <button className="h-12 w-12 rounded-full flex items-center justify-center text-white">
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feature selector dots */}
                  <div className="mt-8 flex justify-center space-x-2">
                    {heroFeatures.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveFeature(index)}
                        className={`h-3 w-3 rounded-full transition-all duration-300 ${
                          activeFeature === index 
                            ? 'bg-white w-6' 
                            : 'bg-white/50 hover:bg-white/70'
                        }`}
                        aria-label={`View feature ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-white animate-bounce hidden sm:flex">
              <p className="text-sm mb-2">Scroll to explore</p>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
            </main>
        </div>
      </div>

      {/* Feature Cards section */}
      <div className="relative bg-white py-24">
        {/* Decorative elements */}
        <div className="hidden lg:block absolute top-0 right-0 -mt-20 w-1/3 h-64 bg-gradient-to-l from-primary-light/10 to-transparent"></div>
        <div className="hidden lg:block absolute bottom-0 left-0 -mb-20 w-1/3 h-64 bg-gradient-to-r from-primary-dark/10 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-dark to-primary">
              Powerful Features for Your Business
              </span>
            </h2>
            <p className="mt-4 text-xl text-gray-500 max-w-3xl mx-auto">
              Everything you need to manage your business administration efficiently
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {features.map((feature, index) => (
              <div
                key={feature.name}
                className="feature-card group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                onClick={() => handleFeatureClick(feature.name)}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary-light/5 to-primary/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-primary-light to-primary mb-6">
                    <feature.icon className="h-7 w-7 text-white" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.name}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  
                  <div className="mt-6 flex items-center text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span>Learn more</span>
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Dark Feature Section */}
      <div className="bg-dark text-white py-24 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent"></div>
        <div className="absolute -right-64 top-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl"></div>
        <div className="absolute -left-64 bottom-1/4 w-96 h-96 bg-primary-light/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="lg:w-1/2 pr-0 lg:pr-16">
              <h2 className="text-3xl font-extrabold sm:text-4xl mb-6">
                Build a flexible admin
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-primary mt-2">system for your business</span>
              </h2>
              <p className="mt-4 text-gray-300 text-lg">
                Aided directly addresses the common challenges of time-consuming data entry, chasing payments, managing cash flow, and complex reporting. By automating these tasks, it minimizes human error and boosts efficiency.
              </p>
              
              {/* Feature list */}
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-white">
                      <ClockIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-white">Save Time</h3>
                    <p className="mt-2 text-gray-300">Automate repetitive tasks and focus on growing your business.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-white">
                      <ChartBarIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-white">Improve Cash Flow</h3>
                    <p className="mt-2 text-gray-300">Get paid faster with automated invoicing and payment reminders.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-white">
                      <CogIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-white">Streamline Operations</h3>
                    <p className="mt-2 text-gray-300">Manage all your business admin in one centralized platform.</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-10">
                <Link
                  to="/signup"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Get started free <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
            <div className="mt-10 lg:mt-0 lg:w-1/2">
              <div className="relative">
                <div className="relative lg:ml-10">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="transform hover:scale-105 transition-transform duration-300 hover:z-10">
                      <img
                        className="w-full h-auto rounded-2xl shadow-2xl"
                        src="/card-1.png"
                        alt="Business card example"
                      />
                    </div>
                    <div className="transform hover:scale-105 transition-transform duration-300 hover:z-10 mt-12">
                      <img
                        className="w-full h-auto rounded-2xl shadow-2xl"
                        src="/card-2.png"
                        alt="Business card example"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

      {/* Testimonials section */}
      <div className="bg-gray-50 py-24 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-dark to-transparent opacity-5"></div>
        <div className="absolute bottom-0 right-0 w-1/3 h-64 bg-gradient-to-l from-primary-light/10 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-3">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-dark to-primary">
              Trusted by Sole Traders Worldwide
              </span>
            </h2>
            <p className="mt-4 text-xl text-gray-500 mb-16">
              See what our customers have to say about Aided
            </p>
          </div>
          
          <div className="mt-12 grid gap-10 md:grid-cols-3 relative">
            {/* Testimonial cards with hover effects */}
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-8 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl relative overflow-hidden group"
              >
                {/* Decorative accent */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-light to-primary"></div>
                
                {/* Quote icon */}
                <div className="absolute top-6 right-6 text-gray-200 opacity-30 group-hover:opacity-50 transition-opacity">
                  <svg className="h-16 w-16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                
                <div className="flex space-x-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-gray-700 mb-8 relative z-10 text-lg font-light italic">"{testimonial.content}"</p>
                
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 bg-gradient-to-br from-primary-light to-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{testimonial.author}</h4>
                    <p className="text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA button */}
          <div className="mt-16 text-center">
            <Link
              to="/testimonials"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary shadow-md hover:bg-primary-dark transition-all duration-300"
            >
              Read more testimonials <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-dark text-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-gray-50 to-transparent opacity-5"></div>
        <div className="absolute -right-64 top-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute -left-64 bottom-1/3 w-96 h-96 bg-primary-light/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto pt-16 pb-8 px-4 sm:px-6 lg:py-20 lg:px-8 relative z-10">
          <div className="xl:grid xl:grid-cols-3 xl:gap-8">
            <div className="xl:col-span-1">
              <img src="/Invert.png" className="w-40 h-auto mb-6" alt="Aided Logo" />
              <p className="text-gray-300 max-w-xs">
                Your AI-powered business admin solution. Streamline your workflow and improve efficiency.
              </p>
              <div className="mt-8 flex space-x-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">Facebook</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
            
            <div className="mt-12 grid grid-cols-2 gap-8 xl:mt-0 xl:col-span-2">
              <div className="md:grid md:grid-cols-2 md:gap-8">
            <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-light mb-4">Product</h3>
                  <ul className="space-y-3">
                <li>
                      <Link to="/" className="text-gray-300 hover:text-white transition-colors">Features</Link>
                </li>
                <li>
                      <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
                </li>
                <li>
                      <Link to="/signup" className="text-gray-300 hover:text-white transition-colors">Sign Up</Link>
                </li>
              </ul>
            </div>
                <div className="mt-12 md:mt-0">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-light mb-4">Support</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/docs" className="text-gray-300 hover:text-white transition-colors">Documentation</Link>
                    </li>
                    <li>
                      <Link to="/support" className="text-gray-300 hover:text-white transition-colors">Support</Link>
                </li>
                <li>
                      <a href="https://apps.apple.com/ph/app/aided/id6740941037" className="text-gray-300 hover:text-white transition-colors">iOS App</a>
                </li>
                <li>
                      <a href="https://play.google.com/store/apps/details?id=com.aided.app" className="text-gray-300 hover:text-white transition-colors">Android App</a>
                </li>
              </ul>
            </div>
              </div>
              <div className="md:grid md:grid-cols-2 md:gap-8">
            <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-light mb-4">Company</h3>
                  <ul className="space-y-3">
                <li>
                      <Link to="/about" className="text-gray-300 hover:text-white transition-colors">About</Link>
                </li>
                <li>
                      <a href="mailto:support@aided.com.au" className="text-gray-300 hover:text-white transition-colors">Contact</a>
                </li>
              </ul>
            </div>
                <div className="mt-12 md:mt-0">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-light mb-4">Legal</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors">Privacy Policy</Link>
                </li>
                <li>
                      <Link to="/terms" className="text-gray-300 hover:text-white transition-colors">Terms of Service</Link>
                </li>
              </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-700">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Aided. All rights reserved.
            </p>
              <div className="mt-4 md:mt-0 flex space-x-6">
                <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
} 